import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@/shared/icons';
import { useTheme } from '@/contexts/theme';
import { Button } from './Button';
import { Input } from './Input';
import { useEscapeKey } from './Modal';

export interface PeriodRangeButtonProps {
  /** Intervalo aplicado (`yyyy-mm-dd`) — vira o rótulo quando ativo e o rascunho ao abrir */
  start?: string;
  end?: string;
  /** Intervalo personalizado em uso (botão preenchido) */
  active: boolean;
  onApply: (range: { start: string; end: string }) => void;
  /**
   * Legenda colapsável em toolbar (DS "Colapso de legendas"): só o ícone até
   * o toolbar ter largura — o rótulo do período ativo deve aparecer em outro
   * lugar da tela (ex.: chip ao lado do título).
   */
  collapsibleLabel?: boolean;
  /** Prefixo dos testids internos (default: "period-picker") */
  'data-testid'?: string;
}

const POPOVER_WIDTH = 272;

/** yyyy-mm-dd → dd/mm (rótulo compacto do botão ativo) */
const formatDayMonth = (iso: string): string => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;

/**
 * Botão "Personalizado" do seletor de período (F81) + popover de 272px com 2
 * date inputs shared + "Aplicar período". Usado pelo `PeriodPicker` e, sozinho,
 * por telas cujo segmentado não é escolha do usuário (ex.: ranking, período
 * definido pelo admin).
 *
 * Botão SEMPRE 100% opaco (F81: nunca opacity reduzida quando inativo).
 * Popover = dropdown ⇒ TEMA CLARO FIXO: container portalado (document.body,
 * z-[100]) com override das CSS variables pra light (mesma técnica do Modal),
 * e os filhos (Input/Button shared) continuam consumindo var(--theme-*).
 */
export const PeriodRangeButton: React.FC<PeriodRangeButtonProps> = ({
  start,
  end,
  active,
  onApply,
  collapsibleLabel = false,
  'data-testid': testId = 'period-picker',
}) => {
  const { lightTokens } = useTheme();
  const lt = lightTokens.colors;

  const btnRef = useRef<HTMLButtonElement>(null);
  const [showPopover, setShowPopover] = useState(false);
  const [draftStart, setDraftStart] = useState(start ?? '');
  const [draftEnd, setDraftEnd] = useState(end ?? '');
  const [popoverPos, setPopoverPos] = useState({ top: 0, left: 0 });

  const handleClick = (): void => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPopoverPos({
        top: rect.bottom + 8,
        left: Math.max(8, rect.right - POPOVER_WIDTH),
      });
    }
    // Ao abrir, o rascunho reflete o intervalo aplicado atual
    setShowPopover((prev) => {
      if (!prev) {
        setDraftStart(start ?? '');
        setDraftEnd(end ?? '');
      }
      return !prev;
    });
  };

  const applyDisabled = !draftStart || !draftEnd || draftStart > draftEnd;

  const handleApply = (): void => {
    if (applyDisabled) return;
    onApply({ start: draftStart, end: draftEnd });
    setShowPopover(false);
  };

  // Fechar popover ao clicar fora
  useEffect(() => {
    if (!showPopover) return;
    const handler = (e: MouseEvent): void => {
      const target = e.target as Element | null;
      if (!target) return;
      if (!target.closest('[data-period-popover]') && !btnRef.current?.contains(target)) {
        setShowPopover(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showPopover]);

  // Fechar popover com ESC
  useEscapeKey(() => setShowPopover(false), showPopover);

  const label = active && start && end ? `${formatDayMonth(start)} - ${formatDayMonth(end)}` : 'Personalizado';

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        aria-pressed={active}
        aria-haspopup="dialog"
        aria-expanded={showPopover}
        aria-label={collapsibleLabel ? label : undefined}
        onClick={handleClick}
        className={`inline-flex items-center gap-2 ${collapsibleLabel ? 'px-[11px] 2xl:@tb:px-3.5' : 'px-3.5'} h-[38px] text-sm font-semibold rounded-lg border transition-colors whitespace-nowrap flex-shrink-0`}
        style={{
          backgroundColor: active ? 'var(--theme-button-primary)' : 'var(--theme-card-background)',
          color: active ? 'var(--theme-button-primary-text)' : 'var(--theme-card-text)',
          borderColor: active ? 'var(--theme-button-primary)' : 'var(--theme-card-border)',
        }}
        data-testid={`${testId}-custom`}
      >
        <Icon name="calendar" size={14} />
        {collapsibleLabel ? <span className="hidden 2xl:@tb:inline">{label}</span> : label}
      </button>

      {showPopover &&
        createPortal(
          <div
            data-period-popover
            role="dialog"
            aria-label="Período personalizado"
            className="fixed z-[100] rounded-lg shadow-lg p-3"
            style={{
              top: `${popoverPos.top}px`,
              left: `${popoverPos.left}px`,
              width: `${POPOVER_WIDTH}px`,
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              // Override CSS variables pra tema claro (popover = dropdown, sempre light)
              '--theme-text': lt.text.primary,
              '--theme-muted-text': lt.text.muted,
              '--theme-input-background': lt.background.input,
              '--theme-input-text': lt.text.primary,
              '--theme-input-border': lt.border.input,
              '--theme-input-focus': lt.border.focus,
              '--theme-error-color': lt.error,
              '--theme-card-background': lt.background.card,
              '--theme-card-text': lt.text.secondary,
              '--theme-card-border': lt.border.default,
              '--theme-card-muted': lt.text.muted,
              '--theme-button-primary': lt.primary,
              '--theme-button-primary-text': lt.text.inverse,
              '--theme-button-primary-hover': lt.primaryHover,
              '--theme-button-secondary': lt.background.elevated,
              '--theme-button-secondary-text': lt.text.secondary,
              '--theme-button-secondary-hover': lt.border.strong,
              '--theme-button-ghost': 'transparent',
              '--theme-button-ghost-hover': lt.background.elevated,
              '--theme-primary': lt.primary,
              '--theme-border': lt.border.default,
              '--theme-component-background': lt.background.card,
            } as React.CSSProperties}
            data-testid={`${testId}-popover`}
          >
            <div
              className="text-[10px] font-bold uppercase tracking-wider pb-2"
              style={{ color: 'var(--theme-card-muted)' }}
            >
              Período personalizado
            </div>
            <div className="space-y-3 mb-3">
              <div>
                <label
                  className="block text-xs font-medium mb-1"
                  style={{ color: 'var(--theme-text)' }}
                >
                  Início
                </label>
                <Input
                  type="date"
                  value={draftStart}
                  onChange={(e) => setDraftStart(e.target.value)}
                  data-testid={`${testId}-custom-start`}
                />
              </div>
              <div>
                <label
                  className="block text-xs font-medium mb-1"
                  style={{ color: 'var(--theme-text)' }}
                >
                  Fim
                </label>
                <Input
                  type="date"
                  value={draftEnd}
                  onChange={(e) => setDraftEnd(e.target.value)}
                  data-testid={`${testId}-custom-end`}
                />
              </div>
            </div>
            <Button
              variant="primary"
              size="sm"
              fullWidth
              icon="check"
              disabled={applyDisabled}
              onClick={handleApply}
              data-testid={`${testId}-custom-apply`}
            >
              Aplicar período
            </Button>
          </div>,
          document.body,
        )}
    </>
  );
};

PeriodRangeButton.displayName = 'PeriodRangeButton';
